<!--
  DistributorNewOrderPage -- "Add New Order" from the distributor Orders page
  (/distributor/reporting/orders/new).

  Card: Rename "Admin Buyer" to Client Portal and retire the legacy Admin
  Buyer dashboard (Bug Intake #566, https://trello.com/c/9R4TXtuz). Staff
  order entry used to live only on the deleted /admin-buyer/dashboard; Jake
  moved it here (Owner and Sales Executive) and into the Client Portal
  (Client Admin). The route is gated on `order.create`.

  The stores offered are the server's list for the caller,
  `GET /campaign/getReportCampaignList` -- the same scoped list the dashboard
  used (an Owner sees every store of the distributor, a Builder or Sales
  Executive the stores they created or are assigned to). `?store=<id>`
  (passed by the Orders page when its list is filtered to a store) starts on
  that store, but only if it is in the list.
-->
<template>
  <section class="dnew" data-testid="dist-new-order-page">
    <header class="dnew__head">
      <router-link
        :to="{ name: 'distributor_reporting_orders' }"
        class="dnew__back"
        data-testid="dist-new-order-back"
      >
        Back to Orders
      </router-link>
      <h1 class="dnew__title">Add New Order</h1>
      <p class="dnew__lead">
        Place an order for a shopper in one of your stores.
      </p>
    </header>

    <p
      v-if="loadError"
      class="dnew__error"
      data-testid="dist-new-order-stores-error"
      role="alert"
    >
      We could not load your stores. Please reload the page and try again.
    </p>

    <NewOrderFlow
      :stores="stores"
      :stores-loading="loading"
      :initial-store-id="requestedStoreId"
      @exit="backToOrders"
    />
  </section>
</template>

<script>
import axios from "@/axios";
import { describeError } from "@/utils/safeErrorLog";
import NewOrderFlow from "@/components/Order/NewOrderFlow.vue";

export default {
  name: "DistributorNewOrderPage",
  components: { NewOrderFlow },
  data() {
    return {
      stores: [],
      loading: true,
      loadError: false,
    };
  },
  computed: {
    requestedStoreId() {
      const q = this.$route && this.$route.query ? this.$route.query.store : null;
      return typeof q === "string" && q ? q : null;
    },
  },
  created() {
    this.loadStores();
  },
  methods: {
    async loadStores() {
      this.loading = true;
      this.loadError = false;
      try {
        const resp = await axios.get("/campaign/getReportCampaignList");
        const rows = Array.isArray(resp && resp.data) ? resp.data : [];
        this.stores = rows.map((row) => ({ id: row.id, name: row.name }));
      } catch (err) {
        console.error("new order: store list failed", describeError(err));
        this.stores = [];
        this.loadError = true;
      } finally {
        this.loading = false;
      }
    },
    backToOrders() {
      this.$router.push({ name: "distributor_reporting_orders" });
    },
  },
};
</script>

<style scoped>
.dnew {
  max-width: 1200px;
  margin: var(--tf-sp-8, 32px) auto;
  padding: 0 var(--tf-sp-4, 16px);
  color: var(--tf-fg-2, rgba(0, 0, 0, 0.75));
}
.dnew__back {
  display: inline-block;
  margin-bottom: var(--tf-sp-2, 8px);
  color: inherit;
}
.dnew__title {
  font-size: var(--tf-size-h2, 1.5rem);
  font-weight: 600;
  color: var(--tf-fg-1, rgba(0, 0, 0, 0.9));
  margin-bottom: var(--tf-sp-2, 8px);
}
.dnew__lead {
  color: var(--tf-fg-3, rgba(0, 0, 0, 0.55));
  margin-bottom: var(--tf-sp-6, 24px);
}
.dnew__error {
  color: var(--tf-error, #c62828);
  font-weight: 600;
}
</style>
